import { cva } from "class-variance-authority";

import { CommandMan, CommandType, DuctType, State, type FullAirHandlerUnit } from "@eisox/api/core-api";
import { NutIcon, TargetIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { Link } from "@tanstack/react-router";

import WarningIcon from "~/assets/boilerroomWarning.svg?react";
import Commutator from "~/assets/commutator.svg?react";
import { Inconsistency } from "~/components/inconsistency";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { getActuatorState, getSpeedLabel, isAutoManualOrForcing } from "../helpers";
import { AirHandlerUnitDialog } from "./air-handler-unit-dialog";

const NAME = "Indicators";

const indicatorVariants = cva("gap-1.25 flex items-center [&_span]:italic", {
  variants: {
    state: {
      true: "before:mr-1.25 before:h-2.5 before:w-2.5 before:inline-block before:rounded-[10px] before:content-['']",
      false: "before:hidden",
    },
    defect: {
      true: "before:bg-primary-red",
      false: "before:bg-primary-green",
    },
  },
  defaultVariants: {
    state: false,
    defect: false,
  },
});

interface IndicatorsProps {
  buildingId: string;
  airHandlerUnit: FullAirHandlerUnit;
  airHandlerUnits: FullAirHandlerUnit[];
}

export const Indicators: React.FC<IndicatorsProps> = ({ buildingId, airHandlerUnit, airHandlerUnits }) => {
  const { state: stateProp, command, commandType, commandMan, commandAutomate, defect } = airHandlerUnit;

  const { state, warning } = getActuatorState({
    state: stateProp,
    commandMan,
    commandAutomate,
    defect: defect ?? false,
  });

  const supplyAirFlow = airHandlerUnit.flows?.find(flow => flow.type === DuctType.SUPPLY)?.actualSetpoint;
  const returnAirFlow = airHandlerUnit.flows?.find(flow => flow.type === DuctType.RETURN)?.actualSetpoint;

  return (
    <div className="gap-5 flex flex-col text-base">
      {command !== undefined &&
        (() => {
          const autoManualOrForcing = isAutoManualOrForcing(commandMan, command);
          return (
            <p className={indicatorVariants()}>
              {"- "}
              {commandMan === CommandMan.MANUAL && <Commutator />}
              {commandType === CommandType.PERCENTAGE &&
                `${m.actuator_state({ state: autoManualOrForcing })} - ${state?.toFixed(1) ?? "--"} %`}
              {commandType === CommandType.ALL_OR_NOTHING &&
                `${m.actuator_state({ state: autoManualOrForcing })} - ${state !== null ? (state === State.ON ? m.on() : m.off()) : "--"}`}
              {commandType === CommandType.TWO_SPEED &&
                `${m.actuator_state({ state: autoManualOrForcing })} - ${state !== null ? getSpeedLabel(CommandType.TWO_SPEED, state) : "--"}`}
              {commandType === CommandType.THREE_SPEED &&
                `${m.actuator_state({ state: autoManualOrForcing })} - ${state !== null ? getSpeedLabel(CommandType.THREE_SPEED, state) : "--"}`}
              {warning && (
                <Tooltip content={warning}>
                  <WarningIcon />
                </Tooltip>
              )}
              <AirHandlerUnitDialog buildingId={buildingId} airHandlerUnit={airHandlerUnit}>
                <NutIcon className="cursor-pointer" width={20} height={20} color={colors.primary.blue} />
              </AirHandlerUnitDialog>
            </p>
          );
        })()}
      {airHandlerUnit.summerModeState !== undefined && (
        <p className={indicatorVariants()}>
          {`- ${m.season()} :`}
          <span className="italic">{airHandlerUnit.summerModeState ? m.winter() : m.summer()}</span>
          <Link to={"/buildings/$buildingId/settings/buildings/season"} params={{ buildingId }}>
            <NutIcon width={20} height={20} color={colors.primary.blue} />
          </Link>
          <Inconsistency items={airHandlerUnits} property="summerModeState" type="air_handler_unit" />
        </p>
      )}
      {airHandlerUnit.freeCooling && (
        <p className={indicatorVariants()}>
          {`- ${m.free_cooling()} : `}
          <span className="italic">{m.active({ active: airHandlerUnit.freeCooling.state ? "true" : "false" })}</span>
          <Link
            to={"/buildings/$buildingId/air-handler-units/$airHandlerUnitId/settings/free-cooling"}
            params={{ buildingId, airHandlerUnitId: airHandlerUnit.id }}
          >
            <NutIcon style={{ width: 20, height: 20, color: colors.primary.blue }} />
          </Link>
        </p>
      )}
      {airHandlerUnit.elecConso !== undefined && (
        <p className={indicatorVariants({ defect: false })}>
          {m.elec_conso({ e: airHandlerUnit.elecConso.toFixed() })}
        </p>
      )}
      {supplyAirFlow !== undefined && (
        <p className={indicatorVariants()}>
          <TargetIcon />
          {m.supply_air_flow({ s: supplyAirFlow.toFixed() })}
        </p>
      )}
      {returnAirFlow !== undefined && (
        <p className={indicatorVariants()}>
          <TargetIcon />
          {m.return_air_flow({ r: returnAirFlow.toFixed() })}
        </p>
      )}
      {airHandlerUnit.defect !== undefined && (
        <p className={indicatorVariants({ state: true, defect: airHandlerUnit.defect })}>{m.unit_defect()}</p>
      )}
      {airHandlerUnit.autonomousTriggerDetectorDefect !== undefined && (
        <p className={indicatorVariants({ state: true, defect: airHandlerUnit.autonomousTriggerDetectorDefect })}>
          {m.autonomous_trigger_detector_defect()}
        </p>
      )}
      {airHandlerUnit.fireDefect !== undefined && (
        <p className={indicatorVariants({ state: true, defect: airHandlerUnit.fireDefect })}>{m.fire_defect()}</p>
      )}
      {airHandlerUnit.smokeDefect !== undefined && (
        <p className={indicatorVariants({ state: true, defect: airHandlerUnit.smokeDefect })}>{m.smoke_defect()}</p>
      )}
      {airHandlerUnit.communicationDefect !== undefined && (
        <p className={indicatorVariants({ state: true, defect: airHandlerUnit.communicationDefect })}>
          {m.communication_defect()}
        </p>
      )}
    </div>
  );
};

Indicators.displayName = NAME;
